/**
 * Bounded-concurrency fan-out helper, host-agnostic.
 *
 * Lifted from the private copy in
 * `seeds/.../AddSeedByTypeModal/hooks/useAddSeedSubmit.ts` so BOTH the seed
 * fan-out and the Complex-Login identity fan-out share one bounded worker pool
 * (DRY). Generalized from the seed-specific `(seed) => Promise<void>` shape to
 * a generic `(item, index) => Promise<R>` worker whose resolved values are
 * collected — IN INPUT ORDER — into the returned array.
 *
 * Phase 4 will switch `useAddSeedSubmit.ts` to import this shared helper and
 * delete its private copy; the temporary duplication is intentional until then.
 */

// Bound the number of concurrent broker POSTs. Single-row /broker rides a 29s
// Lambda/API-GW cap; a small bounded pool keeps a large list (pasted seeds, or
// many credential identities) from fanning out into an unbounded burst
// (security P0-1(d)).
export const MAX_CONCURRENT_CREDENTIAL_POSTS = 4;

/**
 * Runs `worker` over `items` with at most `limit` workers in flight at once,
 * collecting each worker's resolved value into a results array that preserves
 * INPUT ORDER (result[i] corresponds to items[i], regardless of completion
 * order). A worker that throws rejects the whole run (use a worker that catches
 * and encodes per-item failure when partial-failure semantics are required).
 */
export async function runBounded<T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  limit = MAX_CONCURRENT_CREDENTIAL_POSTS
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;

  const runWorker = async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await worker(items[index], index);
    }
  };

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return results;
}
